import { useToast } from '@components/toast/Toast'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { Check, Gift, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface ReferralCodeFieldProps {
  onReferralApplied?: (referrerName: string) => void
  onReferralCodeChange?: (referralCode: string | null) => void
}

/**
 * shadcn-styled referral code input. Mirrors the logic of the original Ant
 * ReferralCodeInput (URL pre-fill + auto-validate, validate on blur/Enter via
 * referrals/check-availability) but without an Ant Form dependency, so it works
 * inside the react-hook-form based register screen.
 */
export function ReferralCodeField({
  onReferralApplied,
  onReferralCodeChange
}: ReferralCodeFieldProps) {
  const { t } = useTranslation('auth')
  const toast = useToast()
  const [searchParams] = useSearchParams()
  const [expanded, setExpanded] = useState(() => !!searchParams.get('ref'))
  const [referralCode, setReferralCode] = useState('')
  const [referralApplied, setReferralApplied] = useState(false)
  const [codeFromUrl, setCodeFromUrl] = useState(false)
  const [referrerName, setReferrerName] = useState<string | null>(null)

  useEffect(() => {
    const refParam = searchParams.get('ref')
    if (refParam) {
      const code = refParam.toUpperCase()
      setReferralCode(code)
      setCodeFromUrl(true)
      onReferralCodeChange?.(code)
      validateCode(code, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const validateCode = async (code: string, fromUrl = false) => {
    if (!code || referralApplied) return
    try {
      const { data } = await apiClient.post(
        SNA_PREFIX + 'referrals/check-availability',
        { referral_code: code }
      )
      if (data.available) {
        const name = data.referrer_name || 'Partner'
        setReferralApplied(true)
        setReferrerName(name)
        if (!fromUrl) {
          toast.success({
            message: t('register.referral.validCode', { referrer: name })
          })
        }
        onReferralApplied?.(name)
      } else if (!fromUrl) {
        toast.error({ message: t('register.referral.invalidCode') })
      }
    } catch (error) {
      if (fromUrl) {
        console.warn('Failed to validate referral code from URL:', error)
      } else {
        toast.error({ message: t('register.referral.error') })
      }
    }
  }

  const handleCollapse = () => {
    setExpanded(false)
    setReferralCode('')
    setReferralApplied(false)
    setReferrerName(null)
    onReferralCodeChange?.(null)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().trim()
    setReferralCode(value)
    onReferralCodeChange?.(value || null)
    if (referralApplied) {
      setReferralApplied(false)
      setReferrerName(null)
    }
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="inline-flex items-center gap-2 text-sm font-medium text-primary transition-colors hover:text-primary-dark"
      >
        <Gift className="h-4 w-4" />
        {t('register.referral.toggle')}
      </button>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Gift className="h-4 w-4 text-primary" />
          {t('register.referral.title')}
        </Label>
        {!codeFromUrl && (
          <button
            type="button"
            onClick={handleCollapse}
            aria-label={t('register.referral.remove')}
            className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-ink/5 hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="relative">
        <Input
          placeholder={t('register.referral.placeholder')}
          value={referralCode}
          onChange={handleChange}
          onKeyDown={(e) => e.key === 'Enter' && validateCode(referralCode)}
          onBlur={() => validateCode(referralCode)}
          disabled={referralApplied || codeFromUrl}
          maxLength={20}
          className="pr-10 uppercase"
        />
        {referralApplied && (
          <Check className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-primary" />
        )}
      </div>
      {referralApplied && referrerName && (
        <p className="rounded-xl bg-primary/10 px-3 py-2 text-sm text-primary">
          {codeFromUrl
            ? t('register.referral.appliedFromLinkDescription', {
                referrer: referrerName,
                code: referralCode
              })
            : t('register.referral.appliedDescription', {
                referrer: referrerName
              })}
        </p>
      )}
    </div>
  )
}
